import React, { useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { MdArrowBack, MdHistory, MdIosShare } from 'react-icons/md';
import { Button, ConfirmDialog, IconButton, TagPill, CONFIRM_DIALOG_TONE_CAUTION } from '@aibuild/ui';
import type { AppVersionSummary, UserAppDraft } from '@api/appBuilder';
import { FullscreenButton } from '../appFrame/FullscreenArea';
import type { FullscreenControls } from '../appFrame/useFullscreen';
import { localized } from '../appRunView';
import AppActionsMenu, { type AppAction } from './AppActionsMenu';
import BuilderModeBadge from './BuilderModeBadge';
import AppLogoTile from './logo/AppLogoTile';
import PublishButton from './PublishButton';
import ShareAppDialog from './share/ShareAppDialog';
import { HeaderActions, HeaderBar, HeaderChips, HeaderTitle } from './AppBuilderHeader.styled';

interface AppBuilderHeaderProps {
  readonly draft: UserAppDraft;
  readonly versions: readonly AppVersionSummary[];
  readonly restoreDisabled: boolean;
  readonly compact: boolean;
  readonly isPublishing: boolean;
  readonly isRestoring: boolean;
  /** Publish sits here only while the chat column, where it lives, is out of sight. */
  readonly showPublish: boolean;
  readonly fullscreen?: FullscreenControls;
  readonly onBack: () => void;
  readonly onRestore: (version: AppVersionSummary) => Promise<void>;
  readonly onPublish: () => void;
}

const versionMeta = (intl: IntlShape, version: AppVersionSummary, restorable: boolean) => {
  if (!version.live) {
    return intl.formatMessage({ id: 'apps.builder.versions.restore', defaultMessage: 'Restore' });
  }
  return restorable
    ? intl.formatMessage({ id: 'apps.builder.versions.liveRestore', defaultMessage: 'Live · Restore' })
    : intl.formatMessage({ id: 'apps.builder.versions.live', defaultMessage: 'Live' });
};

const AppBuilderHeader: React.FC<AppBuilderHeaderProps> = ({
  draft,
  versions,
  restoreDisabled,
  compact,
  isPublishing,
  isRestoring,
  showPublish,
  fullscreen,
  onBack,
  onRestore,
  onPublish,
}) => {
  const intl = useIntl();
  const [pendingRestore, setPendingRestore] = useState<AppVersionSummary | null>(null);
  const [sharing, setSharing] = useState(false);
  const published = draft.publishedVersion != null;
  const disabled = draft.status === 'DISABLED';

  const pickVersion = (version: AppVersionSummary) => {
    if (draft.hasUnpublishedChanges) setPendingRestore(version);
    else onRestore(version).catch(() => undefined);
  };

  const versionActions = versions.map((version): AppAction => {
    const restorable = !version.live || draft.hasUnpublishedChanges;
    return {
      id: `version-${version.version}`,
      label: `v${version.version} · ${intl.formatDate(version.publishedAt, { dateStyle: 'medium', timeStyle: 'short' })}`,
      icon: <MdHistory size={16} />,
      meta: versionMeta(intl, version, restorable),
      disabled: restoreDisabled || !restorable,
      onSelect: () => pickVersion(version),
    };
  });
  const shareLabel = intl.formatMessage({ id: 'apps.builder.share', defaultMessage: 'Share' });

  return (
    <HeaderBar data-testid="app-builder__header">
      <IconButton
        ariaLabel={intl.formatMessage({ id: 'apps.builder.back', defaultMessage: 'Back to apps' })}
        icon={<MdArrowBack size={18} />}
        variant="ghost"
        onClick={onBack}
        dataTestId="app-builder__back"
      />
      <AppLogoTile appKey={draft.appKey} logo={draft.preview.presentation?.logo ?? null} disabled={disabled} />
      <HeaderTitle>{localized(intl, draft.manifest.name)}</HeaderTitle>
      <HeaderChips>
        <BuilderModeBadge draft={draft} compact={compact} />
        {published && draft.hasUnpublishedChanges && !compact && (
          <TagPill
            label={intl.formatMessage({ id: 'apps.builder.status.unpublishedChanges', defaultMessage: 'Unpublished changes' })}
            prefix={false}
            size="sm"
            dataTestId="app-builder__unpublished"
          />
        )}
      </HeaderChips>
      <HeaderActions>
        {versionActions.length > 0 && (
          <AppActionsMenu
            inline
            ariaLabel={intl.formatMessage({ id: 'apps.builder.versions', defaultMessage: 'Published versions' })}
            icon={<MdHistory size={18} />}
            actions={versionActions}
            dataTestId="app-builder__versions"
          />
        )}
        {published &&
          (compact ? (
            <IconButton
              ariaLabel={shareLabel}
              icon={<MdIosShare size={18} aria-hidden="true" />}
              variant="ghost"
              disabled={disabled}
              onClick={() => setSharing(true)}
              dataTestId="app-builder__share"
            />
          ) : (
            <Button
              variant="outlined"
              leadingIcon={<MdIosShare size={16} aria-hidden="true" />}
              disabled={disabled}
              onClick={() => setSharing(true)}
              dataTestId="app-builder__share"
            >
              {shareLabel}
            </Button>
          ))}
        {fullscreen && <FullscreenButton fullscreen={fullscreen} />}
        {showPublish && (
          <PublishButton
            draft={draft}
            isPublishing={isPublishing}
            isRestoring={isRestoring}
            onPublish={onPublish}
            iconOnly={compact}
          />
        )}
      </HeaderActions>
      {published && <ShareAppDialog draft={draft} open={sharing} onClose={() => setSharing(false)} />}
      <ConfirmDialog
        open={pendingRestore !== null}
        onClose={() => setPendingRestore(null)}
        onConfirm={() => (pendingRestore ? onRestore(pendingRestore) : undefined)}
        tone={CONFIRM_DIALOG_TONE_CAUTION}
        title={intl.formatMessage(
          { id: 'apps.builder.restore.title', defaultMessage: 'Restore v{version}?' },
          { version: pendingRestore?.version },
        )}
        description={intl.formatMessage({
          id: 'apps.builder.restore.body',
          defaultMessage: 'Your unpublished changes will be replaced by this version. Published versions are not affected.',
        })}
        confirmLabel={intl.formatMessage({ id: 'apps.builder.restore.confirm', defaultMessage: 'Restore' })}
        dataTestId="app-builder__restore-dialog"
      />
    </HeaderBar>
  );
};

export default AppBuilderHeader;
